import { ApplicationError } from "./errors.js";
const millimetresBrand = Symbol("millimetres");
const milligramsBrand = Symbol("milligrams");
const squareMillimetresBrand = Symbol("squareMillimetres");
const cubicMillimetresBrand = Symbol("cubicMillimetres");
export const MAX_PERSISTED_INTEGER = Number.MAX_SAFE_INTEGER;
function persistedInteger(value, field, positive) {
    const minimum = positive ? 1 : 0;
    if (!Number.isSafeInteger(value) || value < minimum) {
        throw new ApplicationError("PERSISTED_INTEGER_INVALID", `${field} must be ${positive ? "a positive" : "a non-negative"} safe integer.`, { details: { field, value, minimum, maximum: MAX_PERSISTED_INTEGER } });
    }
    return value;
}
export function millimetres(value, field = "millimetres") {
    return persistedInteger(value, field, false);
}
export function positiveMillimetres(value, field = "millimetres") {
    return persistedInteger(value, field, true);
}
export function milligrams(value, field = "milligrams") {
    return persistedInteger(value, field, false);
}
export function positiveMilligrams(value, field = "milligrams") {
    return persistedInteger(value, field, true);
}
export function squareMillimetres(value, field = "square millimetres") {
    return persistedInteger(value, field, true);
}
export function cubicMillimetres(value, field = "cubic millimetres") {
    return persistedInteger(value, field, false);
}
const MILLIMETRES_PER_INCH = 25.4;
const MILLIGRAMS_PER_KILOGRAM = 1_000_000;
const MILLIGRAMS_PER_POUND = 453_592.37;
const CUBIC_MILLIMETRES_PER_CUBIC_METRE = 1_000_000_000;
const CUBIC_MILLIMETRES_PER_CUBIC_FOOT = 28_316_846.592;
export function inchesFromMillimetres(value) {
    return value / MILLIMETRES_PER_INCH;
}
export function poundsFromMilligrams(value) {
    return value / MILLIGRAMS_PER_POUND;
}
const POUNDS_PER_SQUARE_FOOT_PER_KILOGRAM_PER_SQUARE_METRE = 0.204_816_143_6;
function formatNumber(value, minimumFractionDigits, maximumFractionDigits, locale) {
    return new Intl.NumberFormat(locale, {
        minimumFractionDigits,
        maximumFractionDigits,
    }).format(value);
}
export function formatLength(value, preference, locale = "en") {
    if (preference === "metric") {
        if (value < 1_000) {
            return `${formatNumber(value, 0, 0, locale)} mm`;
        }
        if (value < 10_000) {
            return `${formatNumber(value / 10, 1, 1, locale)} cm`;
        }
        return `${formatNumber(value / 1_000, 2, 2, locale)} m`;
    }
    const inches = value / MILLIMETRES_PER_INCH;
    if (inches < 36) {
        return `${formatNumber(inches, 1, 1, locale)} in`;
    }
    const feet = inches / 12;
    if (feet < 30) {
        return `${formatNumber(feet, 2, 2, locale)} ft`;
    }
    let wholeFeet = Math.floor(feet);
    let remainingInches = Math.round((inches - wholeFeet * 12) * 10) / 10;
    if (remainingInches === 12) {
        wholeFeet += 1;
        remainingInches = 0;
    }
    return `${formatNumber(wholeFeet, 0, 0, locale)} ft ${formatNumber(remainingInches, 0, 1, locale)} in`;
}
export function formatMass(value, preference, locale = "en") {
    if (preference === "imperial") {
        return `${formatNumber(value / MILLIGRAMS_PER_POUND, 1, 1, locale)} lb`;
    }
    const kilograms = value / MILLIGRAMS_PER_KILOGRAM;
    if (kilograms >= 1_000) {
        return `${formatNumber(kilograms / 1_000, 2, 2, locale)} t`;
    }
    return `${formatNumber(kilograms, 0, 1, locale)} kg`;
}
export function formatVolume(value, preference, locale = "en") {
    if (preference === "imperial") {
        return `${formatNumber(value / CUBIC_MILLIMETRES_PER_CUBIC_FOOT, 1, 1, locale)} ft³`;
    }
    return `${formatNumber(value / CUBIC_MILLIMETRES_PER_CUBIC_METRE, 3, 3, locale)} m³`;
}
export function formatAreaLoad(kilogramsPerSquareMetre, preference, locale = "en") {
    if (!Number.isFinite(kilogramsPerSquareMetre) || kilogramsPerSquareMetre <= 0) {
        throw new ApplicationError("VALIDATION_ERROR", "Area load must be a positive finite number.");
    }
    if (preference === "imperial") {
        return `${formatNumber(kilogramsPerSquareMetre * POUNDS_PER_SQUARE_FOOT_PER_KILOGRAM_PER_SQUARE_METRE, 1, 1, locale)} lb/ft²`;
    }
    return `${formatNumber(kilogramsPerSquareMetre, 0, 0, locale)} kg/m²`;
}
export function parseLength(value, unit) {
    if (!Number.isFinite(value) || value < 0) {
        throw new ApplicationError("VALIDATION_ERROR", "Length input must be a non-negative number.");
    }
    const multiplier = {
        mm: 1,
        cm: 10,
        m: 1_000,
        in: MILLIMETRES_PER_INCH,
        ft: MILLIMETRES_PER_INCH * 12,
    };
    return millimetres(Math.round(value * multiplier[unit]), "length");
}
export function parseMass(value, unit) {
    if (!Number.isFinite(value) || value < 0) {
        throw new ApplicationError("VALIDATION_ERROR", "Mass input must be a non-negative number.");
    }
    const multiplier = {
        mg: 1,
        kg: MILLIGRAMS_PER_KILOGRAM,
        t: MILLIGRAMS_PER_KILOGRAM * 1_000,
        lb: MILLIGRAMS_PER_POUND,
    };
    return milligrams(Math.round(value * multiplier[unit]), "mass");
}
//# sourceMappingURL=units.js.map